import React from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import ActionIcon from '@components/UI/ActionIcon'
import {
  approveAgentSourcedCreator,
  rejectAgentSourcedCreator,
  shortlistAgentSourcedCreator,
  undoAgentSourcedCreatorAction,
  unshortlistAgentSourcedCreator,
} from '@store/agentSourcedCreators/actions'
import { selectAgentSourcedCreatorActionLoading } from '@store/agentSourcedCreators/selectors'
import Theme from '@theme'
import { Checkbox, Icon, Text } from '@tribegroup'
import abbreviateNumber from '@utils/abbreviateNumber'
import { IAgentSourcedCreator } from '../types'
import { resolveCreatorName } from '../utils/resolveCreatorName'
import VibeCheckStatusPill from '../VibeCheckStatusPill/VibeCheckStatusPill'
import {
  ActionGroup,
  AgeCell,
  AgeTextColumn,
  AvatarBlock,
  AvatarImage,
  DemographicsBlock,
  GenderBar,
  GenderBarFemale,
  GenderBarLeftHalf,
  GenderBarMale,
  GenderBarRightHalf,
  GenderLabels,
  HandleLink,
  IconActionButton,
  IdentityBlock,
  IdentityTextColumn,
  LocationCell,
  LocationTextColumn,
  NameBlock,
  PlatformPill,
  ProfileMetaRow,
  RosterPill,
  RowWrapper,
  SelectCell,
} from './AgentSourcedCreatorRow.styled'
import { formatProfileMeta } from './agentSourcedRowMeta'
import AudienceAgeDonut from './AudienceAgeDonut'
import ScoreRing from './ScoreRing'

const PROVIDER_GLYPH: Record<string, string> = {
  instagram: 'social-instagram-colour',
  tiktok: 'social-tiktok-colour',
  youtube: 'social-youtube-colour',
}

interface IProps {
  creator: IAgentSourcedCreator
  campaignId: number
  onSelect?: (creator: IAgentSourcedCreator) => void
  // BRAND-9514 bulk-reject selection mode (owned by AgentSourcedCreatorListView).
  // Only PENDING creators are selectable — decided rows render no checkbox and
  // ignore clicks while selection mode is on.
  selectionMode?: boolean
  selected?: boolean
  onToggleSelect?: (creatorId: number) => void
  // BRAND-9550: the parent escalated to select-all-pending SCOPE mode, where
  // the set is resolved by the BE and no longer corresponds to a per-row id
  // list. Individual toggling is locked until the user cancels — v1 ships
  // without deselect-after-select-all (`except_ids` is a deliberate
  // fast-follow).
  selectionLocked?: boolean
  // BRAND-9587: the decision pill is a Vibe Check artefact. Once a creator has
  // answered their intent link they have moved past that step, so on List and
  // Shortlisted the heart is the only status worth showing. The owning view
  // decides — the row must not infer where it is being rendered.
  showDecisionPill?: boolean
}

const AgentSourcedCreatorRow: React.FC<IProps> = ({
  creator,
  campaignId,
  onSelect,
  selectionMode = false,
  selected = false,
  onToggleSelect,
  selectionLocked = false,
  showDecisionPill = true,
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const isActionLoading = useSelector(selectAgentSourcedCreatorActionLoading(creator.id))
  const { creator_data, performance, ai_rationale, audience } = creator
  // Likers may be null on YouTube; fall back to followers or commenters for demographics.
  const segment = audience?.likers ?? audience?.followers ?? audience?.commenters ?? null

  const isSelectable = creator.status === 'pending'

  const handleRowClick = (): void => {
    // In selection mode a row click toggles the selection (pendings only)
    // instead of opening the drawer; decided rows do nothing. Under a
    // select-all scope (BRAND-9550) nothing is individually toggleable, so
    // the row is inert too — not just the checkbox.
    if (selectionMode) {
      if (isSelectable && !selectionLocked) {
        onToggleSelect?.(creator.id)
      }
      return
    }
    onSelect?.(creator)
  }

  const handleApprove = (e: React.MouseEvent): void => {
    e.stopPropagation()
    // TODO(BRAND-9277): confirm rel value with Platform team
    const link = creator.links?.find((l) => l.rel === 'approve')
    if (!link) return
    dispatch(
      approveAgentSourcedCreator({
        creatorId: creator.id,
        campaignId,
        href: link.href,
        method: link.method,
      }),
    )
  }

  const handleReject = (e: React.MouseEvent): void => {
    e.stopPropagation()
    const link = creator.links?.find((l) => l.rel === 'reject')
    if (!link) return
    dispatch(
      rejectAgentSourcedCreator({
        creatorId: creator.id,
        campaignId,
        href: link.href,
        method: link.method,
      }),
    )
  }

  const handleShortlist = (e: React.MouseEvent): void => {
    e.stopPropagation()
    const link = creator.links?.find((l) => l.rel === 'shortlist')
    if (!link) return
    dispatch(
      shortlistAgentSourcedCreator({
        creatorId: creator.id,
        campaignId,
        href: link.href,
        method: link.method,
      }),
    )
  }

  const handleUnshortlist = (e: React.MouseEvent): void => {
    e.stopPropagation()
    const link = creator.links?.find((l) => l.rel === 'unshortlist')
    if (!link) return
    dispatch(
      unshortlistAgentSourcedCreator({
        creatorId: creator.id,
        campaignId,
        href: link.href,
        method: link.method,
      }),
    )
  }

  // Unavailable metrics render as an em dash, never 0 (BRAND-9360).
  const genderData = segment?.demographics?.gender ?? audience?.overall?.gender ?? null
  const malePct = genderData?.male ?? 0
  const femalePct = genderData?.female ?? 0
  const pickDominant = (groups?: Array<{ range: string; weight: number }> | null) =>
    groups?.length ? groups.reduce((a, b) => (b.weight > a.weight ? b : a)) : undefined
  const dominantAge =
    pickDominant(segment?.demographics?.age_groups) ?? pickDominant(audience?.overall?.age_groups)

  const pickDominantCountry = (
    countries?: Array<{ code: string; name: string; weight: number }> | null,
  ) => (countries?.length ? countries.reduce((a, b) => (b.weight > a.weight ? b : a)) : undefined)
  const dominantCountry =
    pickDominantCountry(segment?.geo?.top_countries) ??
    pickDominantCountry(audience?.overall?.top_countries)

  const countryCode = creator_data?.location?.country?.code
  const profileMeta = formatProfileMeta(creator_data?.age_group, creator_data?.gender, countryCode)
  const { isRealName, displayName } = resolveCreatorName(creator)
  const undoLink = creator.links?.find((l) => l.rel === 'undo')

  return (
    <RowWrapper
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleRowClick()
        }
      }}
      data-testid={`creator-row-${creator.id}`}
      $selectionMode={selectionMode}
    >
      {selectionMode && (
        // Wrap in a cell that stops propagation so the checkbox toggle does
        // not also fire the row-level toggle (double flip).
        <SelectCell onClick={(e) => e.stopPropagation()}>
          {isSelectable && (
            <Checkbox
              checked={selected}
              disabled={selectionLocked}
              onChange={() => {
                if (selectionLocked) return
                onToggleSelect?.(creator.id)
              }}
              aria-label={intl.formatMessage({ id: 'creatorSearch.vibeCheck.row.selectLabel' })}
              data-testid={`row-select-${creator.id}`}
            />
          )}
        </SelectCell>
      )}
      <AvatarBlock>
        <AvatarImage src={creator_data?.picture ?? ''} alt={displayName ?? ''} />
      </AvatarBlock>

      <NameBlock>
        <Text
          customFontSize={0.9375}
          customFontWeight={600}
          color="#4d4d4d"
          truncated
          block
          style={isRealName ? { textTransform: 'capitalize' } : undefined}
        >
          {displayName}
        </Text>
        <ProfileMetaRow>
          {profileMeta && (
            <Text customFontSize={0.8125} color="#b3b3b3" data-testid="creator-row-profile-meta">
              {profileMeta}
            </Text>
          )}
          <PlatformPill $onPlatform={creator.on_platform}>
            {creator.on_platform ? (
              <FormattedMessage id="creatorSearch.row.onPlatform" />
            ) : (
              <FormattedMessage id="creatorSearch.row.offPlatform" />
            )}
          </PlatformPill>
          {creator.metadata?.yt_roster && (
            <RosterPill data-testid="creator-row-roster-pill">
              <FormattedMessage id="creatorSearch.vibeCheck.row.source.yt_roster" />
            </RosterPill>
          )}
        </ProfileMetaRow>
      </NameBlock>

      <IdentityBlock>
        <Icon glyph={PROVIDER_GLYPH[creator.channel_type] ?? 'social-instagram'} size={1.25} />
        <IdentityTextColumn>
          {creator_data?.url ? (
            <HandleLink
              href={creator_data.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <Text customFontSize={0.875} medium color="#4d4d4d" truncated block>
                {creator_data?.handle ?? creator_data?.username}
              </Text>
            </HandleLink>
          ) : (
            <Text customFontSize={0.875} medium color="#4d4d4d" truncated block>
              {creator_data?.handle ?? creator_data?.username}
            </Text>
          )}
          <Text customFontSize={0.8125} color="#cbcbcb" data-testid="creator-row-identity-meta">
            {performance?.followers != null ? abbreviateNumber(performance.followers) : '—'} ·{' '}
            {performance?.engagement_rate != null ? (
              <>
                {(performance.engagement_rate * 100).toFixed(2)}%{' '}
                <FormattedMessage id="creatorSearch.row.engagementRate" />
              </>
            ) : (
              '—'
            )}
          </Text>
        </IdentityTextColumn>
      </IdentityBlock>

      <DemographicsBlock>
        <GenderBar>
          <GenderBarLeftHalf>
            <GenderBarMale style={{ width: `${malePct * 100}%` }} />
          </GenderBarLeftHalf>
          <GenderBarRightHalf>
            <GenderBarFemale style={{ width: `${femalePct * 100}%` }} />
          </GenderBarRightHalf>
        </GenderBar>
        <GenderLabels>
          <span>{genderData ? `${(malePct * 100).toFixed(0)}%` : '—'} Male</span>
          <span>{genderData ? `${(femalePct * 100).toFixed(0)}%` : '—'} Female</span>
        </GenderLabels>
      </DemographicsBlock>

      <AgeCell>
        <AudienceAgeDonut percentage={(dominantAge?.weight ?? 0) * 100} size={40} />
        <AgeTextColumn>
          <Text customFontSize={0.875} lineHeight={1.25} color="#4d4d4d" nowrap block>
            {dominantAge ? `${dominantAge.range} yo` : '—'}
          </Text>
          <Text customFontSize={0.75} lineHeight={1} color="#b3b3b3" block>
            {dominantAge ? `${(dominantAge.weight * 100).toFixed(0)}%` : '—'}
          </Text>
        </AgeTextColumn>
      </AgeCell>

      <LocationCell data-testid="audience-top-location">
        <AudienceAgeDonut percentage={(dominantCountry?.weight ?? 0) * 100} size={40} />
        <LocationTextColumn>
          <Text customFontSize={0.875} lineHeight={1.25} color="#4d4d4d" block>
            {dominantCountry?.name ?? '—'}
          </Text>
          <Text customFontSize={0.75} lineHeight={1} color="#b3b3b3" block>
            {dominantCountry ? `${(dominantCountry.weight * 100).toFixed(0)}%` : '—'}
          </Text>
        </LocationTextColumn>
      </LocationCell>

      <ScoreRing score={ai_rationale?.selection_score} />

      {/* Adversarial finding 3 (BRAND-9514): while selection mode is on, the
          selection bar supersedes ALL row-level actions — hidden entirely,
          mirroring the Inbox pattern (SubmissionCardActionBar renders the
          SelectionMode bar INSTEAD of the per-card actions). Leaving them
          live let a single approve/reject/undo mutate a row underneath an
          active selection. The checkbox (SelectCell above) is the only
          interactive control left. */}
      {/* BRAND-9582 (QA item 17): hiding ActionGroup above also erased the
          only signal a creator was already vibe-checked — render the pill
          READ-ONLY (no onUndo) so decided rows stay distinguishable while
          nothing here can still mutate the row. */}
      {selectionMode && creator.status !== 'pending' && showDecisionPill && (
        <ActionGroup>
          <VibeCheckStatusPill status={creator.status} />
        </ActionGroup>
      )}
      {!selectionMode && (
        <ActionGroup>
          {creator.status !== 'pending' && showDecisionPill ? (
            // Wrap in a span to stop row-level click propagation so the
            // drawer does not open when the Undo button is pressed.
            <span onClick={(e) => e.stopPropagation()}>
              <VibeCheckStatusPill
                status={creator.status}
                onUndo={
                  undoLink
                    ? () =>
                        dispatch(
                          undoAgentSourcedCreatorAction({
                            creatorId: creator.id,
                            campaignId,
                            href: undoLink.href,
                            method: undoLink.method,
                          }),
                        )
                    : undefined
                }
              />
            </span>
          ) : creator.status === 'pending' ? (
            <>
              {creator.links?.find((l) => l.rel === 'reject') && (
                <IconActionButton
                  variant="reject"
                  onClick={handleReject}
                  disabled={isActionLoading}
                  aria-label={intl.formatMessage({
                    id: 'creatorSearch.vibeCheck.row.action.rejectLabel',
                  })}
                  data-testid={`row-reject-${creator.id}`}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13.88 12.13a1.25 1.25 0 0 1 0 1.76 1.25 1.25 0 0 1-1.76 0L8 9.78l-4.12 4.11a1.25 1.25 0 0 1-1.76 0 1.25 1.25 0 0 1 0-1.76L6.23 8 2.12 3.89a1.25 1.25 0 1 1 1.76-1.76L8 6.24l4.12-4.11a1.25 1.25 0 1 1 1.76 1.76L9.77 8l4.11 4.13Z"
                      fill="currentColor"
                    />
                  </svg>
                </IconActionButton>
              )}

              {creator.links?.find((l) => l.rel === 'approve') && (
                <IconActionButton
                  variant="approve"
                  onClick={handleApprove}
                  disabled={isActionLoading}
                  aria-label={intl.formatMessage({
                    id: 'creatorSearch.vibeCheck.row.action.approveLabel',
                  })}
                  data-testid={`row-approve-${creator.id}`}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.43 4.07 7.71 14.79a1.26 1.26 0 0 1-.89.41 1.24 1.24 0 0 1-.89-.37l-5.1-5.1a1 1 0 0 1 1.47-1.34L6.74 12.17l8.84-9.75a1.25 1.25 0 1 1 1.85 1.68Z"
                      fill="currentColor"
                    />
                  </svg>
                </IconActionButton>
              )}
            </>
          ) : null}

          {creator.links?.find((l) => l.rel === 'shortlist') && (
            <ActionIcon
              glyph="heart"
              color="#FC5151"
              size={1.5}
              onClick={handleShortlist}
              disabled={isActionLoading}
              tooltip="Shortlist creator"
              elementName="shortlist"
              hoverBackgroundColor={Theme.primary100Color}
            />
          )}

          {creator.links?.find((l) => l.rel === 'unshortlist') && (
            <ActionIcon
              glyph="heart-solid"
              color="#FC5151"
              size={1.5}
              onClick={handleUnshortlist}
              disabled={isActionLoading}
              tooltip="Remove from shortlist"
              elementName="unshortlist"
              hoverBackgroundColor={Theme.primary100Color}
            />
          )}
        </ActionGroup>
      )}
    </RowWrapper>
  )
}

export { AgentSourcedCreatorRow }
export default AgentSourcedCreatorRow
